import React, { Component } from 'react';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { ArrowLeft, Save, Send, Eye, Mail, Bold, Italic, Link as LinkIcon, Image, List, Heading, Monitor, Smartphone, X, FileText, Users, UserCog, CheckCircle, XCircle, Zap } from 'lucide-react';
import fetch from '../../../core/fetch';
import history from '../../../core/history';
import s from './NewsletterEditor.css';

class NewsletterEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      campaign: null,
      title: '',
      subject: '',
      htmlContent: this.getDefaultTemplate(),
      previewText: '',
      audienceType: 'all_users',
      saving: false,
      sending: false,
      sendProgress: null,
      previewDevice: 'desktop',
      testEmail: '',
      sendingTest: false,
      editMode: false,
      recipientCount: 0,
    };
    this.editorRef = React.createRef();
    this.previewDebounce = null;
  }

  getDefaultTemplate() {
    return `<h2>Hola {userName},</h2>

<p>Nos complace compartir contigo las ultimas novedades de <strong>NAVEGA</strong>.</p>

<p>Escribe aqui el contenido de tu boletin. Puedes usar las variables del panel para personalizar el mensaje.</p>

<h3>Que hay de nuevo</h3>
<ul>
  <li>Nuevas embarcaciones disponibles</li>
  <li>Ofertas especiales para esta temporada</li>
  <li>Mejoras en la plataforma</li>
</ul>

<p>No dudes en contactarnos si tienes alguna pregunta.</p>

<p>Saludos cordiales,<br /><strong>El equipo de NAVEGA</strong></p>`;
  }

  componentDidMount() {
    const { campaignId } = this.props;
    const params = new URLSearchParams(window.location.search);
    const type = params.get('type');
    if (type) {
      this.setState({ audienceType: type }, () => this.loadRecipientCount());
    } else {
      this.loadRecipientCount();
    }
    if (campaignId) {
      this.loadCampaign(campaignId);
    }
  }

  loadRecipientCount = async () => {
    try {
      const resp = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `query { getNewsletterStats { totalSubscribers totalUnsubscribed totalHosts } }`
        }),
        credentials: 'include'
      });
      const { data } = await resp.json();
      if (data && data.getNewsletterStats) {
        const stats = data.getNewsletterStats;
        const count = this.state.audienceType === 'hosts_only'
          ? stats.totalHosts
          : stats.totalSubscribers - stats.totalUnsubscribed;
        this.setState({ recipientCount: count });
      }
    } catch (error) {
      console.error('Error loading recipient count:', error);
    }
  };

  loadCampaign = async (id) => {
    try {
      const resp = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `query { getNewsletterCampaign(id: ${id}) { id title subject htmlContent previewText audienceType status totalRecipients sentCount failedCount sentAt createdAt } }`
        }),
        credentials: 'include'
      });
      const { data } = await resp.json();
      if (data && data.getNewsletterCampaign) {
        const c = data.getNewsletterCampaign;
        this.setState({
          campaign: c,
          title: c.title,
          subject: c.subject,
          htmlContent: c.htmlContent,
          previewText: c.previewText || '',
          audienceType: c.audienceType,
          editMode: c.status !== 'draft',
        }, () => this.loadRecipientCount());
      }
    } catch (error) {
      console.error('Error loading campaign:', error);
    }
  };

  handleSave = async () => {
    const { campaign, title, subject, htmlContent, previewText, audienceType } = this.state;
    if (!title.trim() || !subject.trim() || !htmlContent.trim()) {
      alert('Completa todos los campos obligatorios');
      return;
    }
    
    this.setState({ saving: true });
    try {
      const isNew = !campaign;
      const mutation = isNew
        ? `mutation { createNewsletterCampaign(title: ${JSON.stringify(title)}, subject: ${JSON.stringify(subject)}, htmlContent: ${JSON.stringify(htmlContent)}, previewText: ${JSON.stringify(previewText)}, audienceType: ${JSON.stringify(audienceType)}) { id title status } }`
        : `mutation { updateNewsletterCampaign(id: ${campaign.id}, title: ${JSON.stringify(title)}, subject: ${JSON.stringify(subject)}, htmlContent: ${JSON.stringify(htmlContent)}, previewText: ${JSON.stringify(previewText)}) { id title status } }`;
      
      const resp = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: mutation }),
        credentials: 'include'
      });
      const { data } = await resp.json();
      const result = isNew ? data?.createNewsletterCampaign : data?.updateNewsletterCampaign;
      
      if (result) {
        if (isNew) {
          history.push(`/siteadmin/newsletter/edit/${result.id}`);
          this.setState({ campaign: result });
        }
        alert('Campaña guardada exitosamente');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('Error al guardar la campaña');
    } finally {
      this.setState({ saving: false });
    }
  };

  handleSend = async () => {
    const { campaign, title, subject, htmlContent, recipientCount, audienceType } = this.state;
    if (!campaign) {
      alert('Primero guarda la campaña');
      return;
    }
    if (!title.trim() || !subject.trim() || !htmlContent.trim()) {
      alert('Completa todos los campos obligatorios');
      return;
    }

    const audienceLabel = audienceType === 'hosts_only' ? `${recipientCount} propietarios` : `${recipientCount} suscriptores`;
    if (!confirm(`ATENCION: Vas a enviar esta campaña a ${audienceLabel}.\n\nAsunto: ${subject}\n\nEsta accion NO se puede deshacer. Continuar?`)) return;
    
    this.setState({ sending: true, sendProgress: { percent: 0, sent: 0, failed: 0, total: recipientCount } });
    try {
      await this.handleSave();
      
      const resp = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `mutation { sendNewsletterCampaign(id: ${campaign.id}) { status errorMessage sentCount failedCount } }`
        }),
        credentials: 'include'
      });
      const { data } = await resp.json();
      if (data && data.sendNewsletterCampaign) {
        const result = data.sendNewsletterCampaign;
        if (result.status === 200) {
          this.setState({
            editMode: true,
            sendProgress: {
              percent: 100,
              sent: result.sentCount,
              failed: result.failedCount,
              total: result.sentCount + result.failedCount
            }
          });
          alert(`Campaña enviada! ${result.sentCount} enviados, ${result.failedCount} fallidos.`);
          this.loadCampaign(campaign.id);
        } else {
          alert(`Error: ${result.errorMessage}`);
        }
      }
    } catch (error) {
      console.error('Error sending campaign:', error);
      alert('Error al enviar la campaña');
    } finally {
      this.setState({ sending: false });
    }
  };

  handleSendTest = async () => {
    const { campaign, testEmail } = this.state;
    if (!campaign) {
      alert('Primero guarda la campaña');
      return;
    }
    if (!testEmail.trim()) {
      alert('Ingresa un email de prueba');
      return;
    }
    
    this.setState({ sendingTest: true });
    try {
      const resp = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `mutation { sendTestNewsletter(id: ${campaign.id}, testEmail: ${JSON.stringify(testEmail)}) { status errorMessage } }`
        }),
        credentials: 'include'
      });
      const { data } = await resp.json();
      if (data?.sendTestNewsletter?.status === 200) {
        alert(`Email de prueba enviado a ${testEmail}`);
      } else {
        alert(`Error: ${data?.sendTestNewsletter?.errorMessage || 'Error desconocido'}`);
      }
    } catch (error) {
      alert('Error al enviar email de prueba');
    } finally {
      this.setState({ sendingTest: false });
    }
  };

  insertTag = (tag) => {
    const textarea = this.editorRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = this.state.htmlContent;
    let insertion = '';
    switch (tag) {
      case 'bold': insertion = `<strong>${text.substring(start, end) || 'texto'}</strong>`; break;
      case 'italic': insertion = `<em>${text.substring(start, end) || 'texto'}</em>`; break;
      case 'link': insertion = `<a href="https://" style="color:#0d3b66;font-weight:600;">${text.substring(start, end) || 'enlace'}</a>`; break;
      case 'image': insertion = `<img src="URL_DE_LA_IMAGEN" alt="descripcion" style="max-width:100%;border-radius:12px;margin:16px 0;" />`; break;
      case 'list': insertion = `<ul>\n  <li>Elemento 1</li>\n  <li>Elemento 2</li>\n  <li>Elemento 3</li>\n</ul>`; break;
      case 'heading': insertion = `<h2>${text.substring(start, end) || 'Titulo'}</h2>`; break;
      default: insertion = tag;
    }
    const newText = text.substring(0, start) + insertion + text.substring(end);
    this.setState({ htmlContent: newText });
  };

  getPreviewHTML = () => {
    const { htmlContent, subject, previewDevice, audienceType } = this.state;
    const processed = htmlContent
      .replace(/\{userName\}/g, 'Juan Martinez')
      .replace(/\{firstName\}/g, 'Juan')
      .replace(/\{email\}/g, 'juan@ejemplo.com')
      .replace(/\{siteName\}/g, 'NAVEGA');

    const isHostOnly = audienceType === 'hosts_only';
    const width = previewDevice === 'mobile' ? '375px' : '100%';
    
    return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><style>body{margin:0;background:#f0f2f5;font-family:system-ui,sans-serif;display:flex;justify-content:center;padding:16px;}
.c{width:${width};max-width:600px;}</style></head>
<body><div class="c">
<div style="max-width:600px;margin:0 auto;background:#fff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">
<div style="background:linear-gradient(135deg,#0a1628,#1a2744,#0d3b66);padding:40px 32px;text-align:center;">
<img src="/images/logo/c20067304c1486b07bcce23298d562f8.png" alt="NAVEGA" style="max-width:180px;height:auto;" />
</div>
<div style="padding:40px 32px;color:#2d3748;font-size:16px;line-height:1.7;">
${processed}
</div>
<div style="background:#f7fafc;padding:32px;text-align:center;border-top:1px solid #e2e8f0;">
<a href="#" style="color:#0d3b66;text-decoration:none;font-size:13px;font-weight:600;">navegaapp.com</a>
<p style="margin:12px 0 0;font-size:12px;color:#94a3b8;">&copy; ${new Date().getFullYear()} NAVEGA. Todos los derechos reservados.</p>
<p style="margin:4px 0 0;font-size:12px;color:#94a3b8;">Este correo fue enviado a juan@ejemplo.com</p>
${!isHostOnly ? '<div style="margin-top:16px;padding-top:12px;border-top:1px solid #e2e8f0;"><a href="#" style="color:#c0c8d4;font-size:11px;text-decoration:underline;">Desuscribirse de este boletin</a></div>' : ''}
</div></div></div></body></html>`;
  };

  render() {
    const { title, subject, htmlContent, previewText, audienceType, campaign, saving, sending, sendProgress, previewDevice, testEmail, sendingTest, editMode, recipientCount } = this.state;
    const isDraft = !campaign || campaign.status === 'draft';
    const isHostOnly = audienceType === 'hosts_only';

    return (
      <div className={s.editorPage}>
        <button className={s.backLink} onClick={() => history.push('/siteadmin/newsletter')}>
          <ArrowLeft size={16} /> Volver al Centro de Boletin
        </button>

        {/* Editor Header */}
        <div className={s.editorHeader}>
          <div className={s.editorTitle}>
            {!campaign ? 'Nueva Campaña' : isDraft ? 'Editar Campaña' : 'Detalles de Campaña'}
          </div>
          <div className={s.editorSubtitle}>
            {isDraft ? 'Diseña y envia tu campaña de email' : 'Revisa los detalles y resultados de esta campaña'}
          </div>
          <div className={`${s.audienceBadge} ${isHostOnly ? s.audienceBadgeHosts : s.audienceBadgeGeneral}`}>
            {isHostOnly ? <UserCog size={14} /> : <Users size={14} />}
            {isHostOnly ? 'Comunicado para Propietarios' : 'Boletin General'}
            {isHostOnly ? ' (sin desuscripcion)' : ''}
          </div>
          <div className={s.recipientCount}>
            <Send size={14} /> Se enviara a <strong>{recipientCount}</strong> destinatarios
          </div>
        </div>

        {/* Send Progress */}
        {sendProgress && (
          <div className={s.progressContainer}>
            <div className={s.progressHeader}>
              <div className={s.progressLabel}>
                {sending && <span className={s.sendingPulse} />}
                {sending ? 'Enviando campaña...' : 'Envio completado'}
              </div>
              <div className={s.progressPercent}>{sendProgress.percent}%</div>
            </div>
            <div className={s.progressBar}>
              <div className={s.progressFill} style={{ width: `${sendProgress.percent}%` }} />
            </div>
            <div className={s.progressStats}>
              <div className={`${s.progressStat} ${s.progressStatGreen}`}>
                <CheckCircle size={15} /> {sendProgress.sent} enviados
              </div>
              <div className={`${s.progressStat} ${s.progressStatRed}`}>
                <XCircle size={15} /> {sendProgress.failed} fallidos
              </div>
              <div className={`${s.progressStat} ${s.progressStatGray}`}>
                <Mail size={15} /> {sendProgress.total} total
              </div>
            </div>
          </div>
        )}

        {/* Form Fields */}
        <div className={s.formSection}>
          <div className={s.formSectionTitle}>
            <div className={s.formSectionIcon}><FileText size={16} /></div>
            Informacion de la Campaña
          </div>
          <div className={s.formGroup}>
            <label className={s.formLabel}>Titulo Interno</label>
            <input className={s.formInput} type="text" value={title} onChange={e => this.setState({ title: e.target.value })} placeholder="Ej: Boletin Abril 2026 - Nuevas embarcaciones" disabled={editMode} />
          </div>
          <div className={s.formGroup}>
            <label className={s.formLabel}>Asunto del Email</label>
            <input className={s.formInput} type="text" value={subject} onChange={e => this.setState({ subject: e.target.value })} placeholder="Ej: Descubre las nuevas embarcaciones en NAVEGA" disabled={editMode} />
          </div>
          <div className={s.formGroup}>
            <label className={s.formLabel}>Texto de Vista Previa (opcional)</label>
            <input className={s.formInput} type="text" value={previewText} onChange={e => this.setState({ previewText: e.target.value })} placeholder="Texto que aparece antes de abrir el email en la bandeja..." disabled={editMode} />
          </div>
        </div>

        {/* Editor + Preview Side by Side */}
        <div className={s.editorLayout}>
          <div className={s.editorPane}>
            <div className={s.formSection} style={{ padding: 0 }}>
              <div style={{ padding: '20px 24px 0' }}>
                <div className={s.formSectionTitle}>
                  <div className={s.formSectionIcon}><Mail size={16} /></div>
                  Contenido del Email (HTML)
                </div>
              </div>
              <div className={s.editorContainer} style={{ border: 'none', borderRadius: 0 }}>
                <div className={s.editorToolbar}>
                  <button className={s.toolbarBtn} onClick={() => this.insertTag('heading')} disabled={editMode}><Heading size={14} /> H2</button>
                  <button className={s.toolbarBtn} onClick={() => this.insertTag('bold')} disabled={editMode}><Bold size={14} /></button>
                  <button className={s.toolbarBtn} onClick={() => this.insertTag('italic')} disabled={editMode}><Italic size={14} /></button>
                  <div className={s.toolbarSep} />
                  <button className={s.toolbarBtn} onClick={() => this.insertTag('link')} disabled={editMode}><LinkIcon size={14} /></button>
                  <button className={s.toolbarBtn} onClick={() => this.insertTag('image')} disabled={editMode}><Image size={14} /></button>
                  <button className={s.toolbarBtn} onClick={() => this.insertTag('list')} disabled={editMode}><List size={14} /></button>
                  <div className={s.toolbarGroup}>
                    <span className={s.toolbarGroupLabel}>VAR</span>
                    <button className={s.toolbarBtn} onClick={() => this.insertTag('{userName}')} disabled={editMode}>Nombre</button>
                    <button className={s.toolbarBtn} onClick={() => this.insertTag('{email}')} disabled={editMode}>Email</button>
                  </div>
                </div>
                <textarea ref={this.editorRef} className={s.htmlEditor} value={htmlContent} onChange={e => this.setState({ htmlContent: e.target.value })} placeholder="Escribe el contenido HTML del email aqui..." disabled={editMode} />
              </div>
              <div style={{ padding: '0 24px 16px' }}>
                <div className={s.variableHint}>
                  Variables: <code>{'{userName}'}</code> <code>{'{firstName}'}</code> <code>{'{email}'}</code> <code>{'{siteName}'}</code>
                </div>
              </div>
            </div>
          </div>
          <div className={s.previewPane}>
            <div className={s.previewCard}>
              <div className={s.previewCardHeader}>
                <div className={s.previewCardTitle}><Eye size={15} /> Vista Previa en Vivo</div>
                <div className={s.previewDeviceToggle}>
                  <button className={`${s.previewDeviceBtn} ${previewDevice === 'desktop' ? s.previewDeviceBtnActive : ''}`} onClick={() => this.setState({ previewDevice: 'desktop' })}>
                    <Monitor size={13} /> PC
                  </button>
                  <button className={`${s.previewDeviceBtn} ${previewDevice === 'mobile' ? s.previewDeviceBtnActive : ''}`} onClick={() => this.setState({ previewDevice: 'mobile' })}>
                    <Smartphone size={13} /> Movil
                  </button>
                </div>
              </div>
              <div className={s.previewCardBody}>
                <iframe
                  className={s.previewFrame}
                  srcDoc={this.getPreviewHTML()}
                  title="Email Preview"
                  style={{ maxWidth: previewDevice === 'mobile' ? '375px' : '100%' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className={s.actionsBar}>
          <div className={s.actionsLeft}>
            {campaign && isDraft && (
              <>
                <input className={s.testEmailInput} type="email" value={testEmail} onChange={e => this.setState({ testEmail: e.target.value })} placeholder="Email de prueba..." />
                <button className={s.btnSecondary} onClick={this.handleSendTest} disabled={sendingTest}>
                  <Mail size={14} /> {sendingTest ? 'Enviando...' : 'Enviar Prueba'}
                </button>
              </>
            )}
          </div>
          <div className={s.actionsRight}>
            {isDraft && (
              <>
                <button className={s.btnPrimary} onClick={this.handleSave} disabled={saving}>
                  <Save size={14} /> {saving ? 'Guardando...' : 'Guardar'}
                </button>
                <button className={`${s.btnPrimary} ${s.btnSend}`} onClick={this.handleSend} disabled={sending || !campaign}>
                  <Send size={14} /> {sending ? 'Enviando...' : `Enviar a ${recipientCount} destinatarios`}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default withStyles(s)(NewsletterEditor);
